import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ImagePlus, Link2, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, media } from "@/lib/api";
import { ConfirmDialog, EmptyState, Field } from "@/components/admin/AdminUI";

export const ProductImages = ({ product, onChange, canWrite }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [urlForm, setUrlForm] = useState({ url: "", alt_text: "", color: "", license_note: "" });
  const [del, setDel] = useState(null);
  const [alt, setAlt] = useState({});
  const images = product?.images || [];

  if (!product?.id) return <EmptyState title="Save the product first" description="Imagery can be uploaded once the product record exists." />;

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt_text", "");
      try {
        await api.post(`/admin/products/${product.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err, "upload failed")}`);
      }
    }
    if (ok) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded`);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    onChange();
  };

  const addUrl = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/products/${product.id}/images/url`, { ...urlForm, color: urlForm.color || null });
      toast.success("Image added");
      setUrlOpen(false);
      setUrlForm({ url: "", alt_text: "", color: "", license_note: "" });
      onChange();
    } catch (err) {
      toast.error(errMsg(err, "Could not add image"));
    } finally {
      setBusy(false);
    }
  };

  const move = async (idx, dir) => {
    const ids = images.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      await api.post(`/admin/products/${product.id}/images/reorder`, { ids });
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveAlt = async (img) => {
    const v = alt[img.id];
    if (v === undefined || v === img.alt_text) return;
    try {
      await api.put(`/admin/images/${img.id}`, { alt_text: v, color: img.color || null });
      toast.success("Alt text saved");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/images/${del.id}`);
      toast.success("Image removed");
      setDel(null);
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="product-images-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">{images.length} image{images.length === 1 ? "" : "s"} · the first image is the storefront hero. JPEG, PNG or WebP up to 10 MB.</p>
        {canWrite && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="image-file-input" />
            <Button variant="outline" onClick={() => setUrlOpen(true)} className="rounded-lg" data-testid="image-add-url-button"><Link2 className="h-4 w-4 mr-2" />Add by URL</Button>
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-lg" data-testid="image-upload-button"><Upload className="h-4 w-4 mr-2" />{busy ? "Uploading…" : "Upload"}</Button>
          </div>
        )}
      </div>

      {images.length === 0 ? (
        <div className="bg-card border border-dashed rounded-xl">
          <EmptyState icon={ImagePlus} title="No imagery yet" description="Upload studio shots or reference an https image. At least one image is required before publishing." action={canWrite && <Button onClick={() => fileRef.current?.click()} data-testid="image-empty-upload-button"><Upload className="h-4 w-4 mr-2" />Upload image</Button>} />
        </div>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-images-grid">
          {images.map((img, idx) => (
            <li key={img.id} className="bg-card border rounded-xl overflow-hidden group" data-testid="product-image-card">
              <div className="relative aspect-[3/4] bg-secondary">
                <img src={media(img.url, 600)} alt={img.alt_text || ""} className="h-full w-full object-cover" />
                {idx === 0 && <span className="absolute left-2 top-2 rounded-full bg-foreground text-background text-[10px] tracking-[0.12em] uppercase px-2 py-0.5">Hero</span>}
                {img.source === "external" && <span className="absolute right-2 top-2 rounded-full bg-card/90 border text-[10px] tracking-wide px-2 py-0.5">URL</span>}
                {canWrite && (
                  <div className="absolute inset-x-2 bottom-2 flex items-center justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <div className="flex gap-1">
                      <Button size="icon" variant="secondary" className="h-8 w-8 bg-card/95" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Move earlier" data-testid="image-move-left"><ArrowLeft className="h-4 w-4" /></Button>
                      <Button size="icon" variant="secondary" className="h-8 w-8 bg-card/95" disabled={idx === images.length - 1} onClick={() => move(idx, 1)} aria-label="Move later" data-testid="image-move-right"><ArrowRight className="h-4 w-4" /></Button>
                    </div>
                    <Button size="icon" variant="secondary" className="h-8 w-8 bg-card/95 text-destructive" onClick={() => setDel(img)} aria-label="Delete image" data-testid="image-delete-button"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
              <div className="p-3">
                <Input value={alt[img.id] ?? img.alt_text ?? ""} onChange={(e) => setAlt({ ...alt, [img.id]: e.target.value })} onBlur={() => saveAlt(img)} disabled={!canWrite} placeholder="Alt text" className="h-8 text-xs" aria-label="Alt text" data-testid="image-alt-input" />
                {img.color && <p className="text-[11px] text-muted-foreground mt-1.5">Colour: {img.color}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={urlOpen} onOpenChange={setUrlOpen}>
        <DialogContent data-testid="image-url-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium">Add image by URL</DialogTitle>
            <DialogDescription>Reference an https image you have the rights to use. Record the licence for compliance.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addUrl} className="space-y-4 pt-2">
            <Field label="Image URL" htmlFor="img-url" required><Input id="img-url" type="url" required value={urlForm.url} onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })} placeholder="https://…" data-testid="image-url-input" /></Field>
            <Field label="Alt text" htmlFor="img-alt"><Input id="img-alt" value={urlForm.alt_text} onChange={(e) => setUrlForm({ ...urlForm, alt_text: e.target.value })} data-testid="image-url-alt-input" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Colour shown" htmlFor="img-color"><Input id="img-color" value={urlForm.color} onChange={(e) => setUrlForm({ ...urlForm, color: e.target.value })} /></Field>
              <Field label="Licence note" htmlFor="img-lic"><Input id="img-lic" value={urlForm.license_note} onChange={(e) => setUrlForm({ ...urlForm, license_note: e.target.value })} placeholder="Brand-supplied asset" /></Field>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUrlOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="image-url-save-button">{busy ? "Adding…" : "Add image"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Remove this image?" description="The image is soft-deleted and disappears from the storefront immediately." confirmLabel="Remove image" destructive busy={busy} onConfirm={remove} />
    </div>
  );
};
